import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import {
  BestDoctorReviewQuery,
  GetDoctorReviewQuery,
  GetDoctorReviewQueryVariables,
  GetHospitalByIdQuery,
  UnlikeReviewMutationVariables,
} from '@/types/bff/gql-schema';

import useGetHospitalById from '@/hooks/hospital/useGetHospitalById';
import { useBestDoctorReviewQuery, useUnlikeReviewMutation } from './doctor.gql.impl';
import useGetDoctorReviewInfinityQuery from './useGetDoctorReviewInfinityQuery';
import { GetDoctorReviewsParams, getDoctorReviewSort } from './useGetDoctorReviews';

const useUnlikeReview = (params: GetDoctorReviewsParams & { hospitalId?: string }) => {
  const queryClient = useQueryClient();

  const doctorReviewRequest = {
    doctorReviewRequest: {
      doctorPublicId: params.doctorId,
      diagnosisTypeCriteria: params.diagnosisTypeCriteria,
    },
  };

  const doctorReviewsKey = {
    ...doctorReviewRequest,
    doctorReviewSort: getDoctorReviewSort(params.sort),
    pagingRequest: {
      page: 1,
      limit: 10,
    },
  };

  /**
   * Normal Review Unlike
   */

  // Normal Review Unlike onMutate by doctorPid
  const onMutateNormalReviewUnlikeByPid = (mutateParams: UnlikeReviewMutationVariables) => {
    const previousReviews: InfiniteData<GetDoctorReviewQuery> | undefined = queryClient.getQueryData(
      useGetDoctorReviewInfinityQuery.getKey(doctorReviewsKey as GetDoctorReviewQueryVariables)
    );

    queryClient.setQueryData(
      useGetDoctorReviewInfinityQuery.getKey(doctorReviewsKey as GetDoctorReviewQueryVariables),
      {
        pages: previousReviews?.pages?.map((page) => {
          return {
            doctorReview: page.doctorReview.map((review) => {
              if (review.reviewId === mutateParams.reviewId) {
                return {
                  ...review,
                  isLiked: false,
                  likeCount: review.likeCount - 1,
                };
              }
              return review;
            }),
          };
        }),
      }
    );
  };

  // Normal Review Unlike Mutate
  const { mutate: mutateNormalReviewUnlike } = useUnlikeReviewMutation({
    onMutate: onMutateNormalReviewUnlikeByPid,
  });

  /**
   * Best Review Unlike
   */

  // Best Review Unlike onMutate by doctorPid
  const onMutateBestReviewUnlikeByPid = () => {
    const queryKey = useBestDoctorReviewQuery.getKey({
      doctorPublicId: params.doctorId,
    });
    const previousReviews: BestDoctorReviewQuery | undefined = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, {
      bestDoctorReview: {
        ...previousReviews?.bestDoctorReview,
        isLiked: false,
        likeCount: previousReviews?.bestDoctorReview?.likeCount ? previousReviews?.bestDoctorReview?.likeCount - 1 : 0,
      },
    });
  };

  // Best Review Unlike Mutate
  const { mutate: mutateBestReviewUnlike } = useUnlikeReviewMutation({
    onMutate: onMutateBestReviewUnlikeByPid,
  });

  const onMutateHospitalTopRatedReviewUnlike = (mutateParams: UnlikeReviewMutationVariables) => {
    if (!params.hospitalId) {
      console.warn('Hospital ID not provided for top rated review unlike');
      return;
    }

    const hospitalQueryKey = useGetHospitalById.getKey(params.hospitalId);
    const previousHospitalData = queryClient.getQueryData<GetHospitalByIdQuery>(hospitalQueryKey);

    if (!previousHospitalData) {
      console.warn('No hospital data found in cache');
      return;
    }

    const updatedTopRatedReviews = previousHospitalData.getHospitalByPublicId.topRatedDoctorReviews.map((review) => {
      if (review.id === mutateParams.reviewId) {
        return {
          ...review,
          isLiked: false,
          likeCount: Math.max(0, review.likeCount - 1), // 0 이하로 내려가지 않도록
        };
      }
      return review;
    });

    const updatedHospitalData: GetHospitalByIdQuery = {
      ...previousHospitalData,
      getHospitalByPublicId: {
        ...previousHospitalData.getHospitalByPublicId,
        topRatedDoctorReviews: updatedTopRatedReviews,
      },
    };

    queryClient.setQueryData(hospitalQueryKey, updatedHospitalData);
  };

  const { mutate: mutateHospitalTopRatedReviewUnlike } = useUnlikeReviewMutation({
    onMutate: onMutateHospitalTopRatedReviewUnlike,
  });

  const bestReviewUnlikeMutate = (reviewId: string) => {
    return mutateBestReviewUnlike({
      reviewId,
    });
  };

  const normalReviewUnlikeMutate = (reviewId: string) => {
    return mutateNormalReviewUnlike({
      reviewId,
    });
  };

  const hospitalTopRatedReviewUnlikeMutate = (reviewId: string) => {
    return mutateHospitalTopRatedReviewUnlike({
      reviewId,
    });
  };

  return {
    bestReviewUnlikeMutate,
    normalReviewUnlikeMutate,
    hospitalTopRatedReviewUnlikeMutate,
  };
};

export default useUnlikeReview;
